import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Form } from 'react-bootstrap';
import { CheckLg, XLg, Pencil } from 'react-bootstrap-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import SubscriptionManagement from './SubscriptionManagement';
import AlertToast from './AlertToast';
import AlertBanner from './AlertBanner';
import api from '../services/api';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const { currentUser, userProfile, updateUserProfile, sendVerificationEmail, updateEmailNotifications } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [editMode, setEditMode] = useState(false);
  const [displayName, setDisplayName] = useState(userProfile?.displayName || '');
  const [selectedRole, setSelectedRole] = useState(userProfile?.role || '');
  const [emailNotifications, setEmailNotifications] = useState(userProfile?.emailNotifications ?? true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordResetEmailSent, setPasswordResetEmailSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  // Handle success message from location state (e.g., after password change)
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccess(location.state.successMessage);
      setEditMode(false); // Exit edit mode
      setPasswordResetEmailSent(false); // Reset password email state
      // Clear the location state to prevent showing the message again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Countdown timer for resend email
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendTimer]);

  const handleUpdateProfile = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await updateUserProfile(displayName, selectedRole);
      setSuccess('Your profile has been updated.');
      setEditMode(false);
    } catch (error: any) {
      setError('Failed to update profile.');
      console.error('Update profile error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setDisplayName(userProfile?.displayName || '');
    setSelectedRole(userProfile?.role || '');
    setEditMode(false);
  };

  const handleSendVerification = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await sendVerificationEmail();
      setSuccess('Verification email sent. Please check your inbox.');
    } catch (error: any) {
      if (error.code === 'auth/too-many-requests') {
        setError('Too many requests. Please wait before trying again.');
      } else {
        setError('Failed to send verification email');
      }
      console.error('Verification email error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailNotificationsChange = async (checked: boolean) => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await updateEmailNotifications(checked);
      setEmailNotifications(checked);
      setSuccess('Your profile has been updated.');
    } catch (error: any) {
      setError('Failed to update email preferences.');
      console.error('Update email notifications error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendPasswordResetEmail = async () => {
    if (resendTimer > 0) return;

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await api.post('/api/password-reset/request', {
        email: currentUser?.email,
        fromDashboard: true
      });

      setPasswordResetEmailSent(true);
      setResendTimer(60);
    } catch (error: any) {
      setError('Failed to send password reset email. Please try again.');
      console.error('Password reset email error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser || !userProfile) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Loading user profile...</Alert>
      </Container>
    );
  }

  return (
    <>
      {downloadSuccess && (
        <AlertBanner
          message="Your account data has been downloaded."
          type="success"
          onClose={() => setDownloadSuccess(false)}
        />
      )}
      {success && (
        <AlertToast
          message={success}
          type="success"
          onClose={() => setSuccess('')}
        />
      )}
      {error && (
        <AlertToast
          message={error}
          type="error"
          onClose={() => setError('')}
        />
      )}
      <Container className="dashboard-container">
        <Row>
          <Col md={8}>
            <div className="dashboard-header">
              <h1>profile & settings</h1>
              <p className="lead">Manage your profile and settings here</p>
            </div>
          
          <Card className="dashboard-card mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="card-title">Profile</h5>
              {editMode ? (
                <div className="edit-actions">
                  <button
                    className="edit-action-btn save-btn"
                    onClick={() => handleUpdateProfile()}
                    disabled={loading}
                    type="button"
                  >
                    <CheckLg />
                  </button>
                  <button
                    className="edit-action-btn cancel-btn"
                    onClick={handleCancelEdit}
                    disabled={loading}
                    type="button"
                  >
                    <XLg />
                  </button>
                </div>
              ) : (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setEditMode(true)}
                >
                  <Pencil className="me-1" /> Edit Profile
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {!currentUser.emailVerified && (
                <Alert variant="warning" className="d-flex justify-content-between align-items-center">
                  <span>Your email is not verified.</span>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={handleSendVerification}
                    disabled={loading}
                  >
                    Send Verification Email
                  </Button>
                </Alert>
              )}

              {editMode ? (
                <div className="profile-info-container">
                  <div className="user-avatar">
                    {userProfile.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                  <div className="profile-edit-form">
                    <div className="edit-row">
                      <Form.Group className="edit-field">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          required
                          disabled={loading}
                        />
                      </Form.Group>

                      <Form.Group className="edit-field">
                        <Form.Label>Role</Form.Label>
                        <Form.Select
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value)}
                          disabled={loading}
                        >
                          <option value="">Select your role</option>
                          <option value="UX/UI Designer">UX/UI Designer</option>
                          <option value="Researcher">Researcher</option>
                          <option value="Strategist">Strategist</option>
                          <option value="Service Designer">Service Designer</option>
                          <option value="Accessibility Specialist">Accessibility Specialist</option>
                          <option value="Content Strategist">Content Strategist</option>
                          <option value="Program Manager">Program Manager</option>
                          <option value="Developer">Developer</option>
                          <option value="Product Manager">Product Manager</option>
                          <option value="Product Owner">Product Owner</option>
                          <option value="Design Manager">Design Manager</option>
                          <option value="Creative Director">Creative Director</option>
                          <option value="Brand Strategist">Brand Strategist</option>
                          <option value="QA / Tester">QA / Tester</option>
                          <option value="Product Designer">Product Designer</option>
                          <option value="Other">Other</option>
                        </Form.Select>
                      </Form.Group>
                    </div>

                    <div className="info-row">
                      <span className="info-label">EMAIL:</span>
                      <span className="info-value">{currentUser.email?.toUpperCase()}</span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">PASSWORD:</span>
                      <span className="info-value">********</span>
                    </div>

                    <div className="change-password-row">
                      {passwordResetEmailSent ? (
                        <>
                          <span className="password-reset-success-text">
                            A password reset email has been sent to your inbox
                          </span>
                          {resendTimer > 0 ? (
                            <span className="password-reset-timer-text">
                              ({resendTimer}s)
                            </span>
                          ) : (
                            <Button
                              variant="link"
                              className="change-password-btn change-password-btn-resend"
                              onClick={handleSendPasswordResetEmail}
                              disabled={loading}
                            >
                              RESEND EMAIL
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          variant="link"
                          className="change-password-btn"
                          onClick={handleSendPasswordResetEmail}
                          disabled={loading}
                        >
                          CHANGE PASSWORD
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="profile-info-container">
                  <div className="user-avatar">
                    {userProfile.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                  <div className="profile-details">
                    <div className="info-row">
                      <span className="info-label">Name:</span>
                      <span className="info-value">{userProfile.displayName}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Role:</span>
                      <span className="info-value">{userProfile.role || 'User'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Email:</span>
                      <span className="info-value">{userProfile.email}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Password:</span>
                      <span className="info-value">********</span>
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="dashboard-card mt-4">
            <Card.Header>
              <h5 className="card-title">Settings</h5>
            </Card.Header>
            <Card.Body>
              {/* <div className="d-flex justify-content-between align-items-center mb-3">
                <span>Dark Mode</span>
                <Form.Check
                  type="switch"
                  id="darkModeSwitch"
                  checked={isDarkMode}
                  onChange={toggleDarkMode}
                  label=""
                />
              </div> */}
              <div className="d-flex justify-content-between align-items-center">
                <span>Receive email notifications</span>
                <Form.Check
                  type="switch"
                  id="emailNotificationsSwitch"
                  checked={emailNotifications}
                  onChange={(e) => handleEmailNotificationsChange(e.target.checked)}
                  disabled={loading}
                  label=""
                />
              </div>
            </Card.Body>
          </Card>

          <SubscriptionManagement onDownloadSuccess={() => setDownloadSuccess(true)} />
        </Col>
      </Row>
    </Container>
    </>
  );
};

export default Dashboard;